// /groups/:slug — customer-facing business profile page. Renders whatever
// the group's public listing shows: name, description, category, address,
// hours, contact, map link, reviews, recent posts, claim CTA.
//
// Anon-safe: hits /api/public/groups/{slug} + the review/posts endpoints
// under /api/public. Authed users get the same data + additional CTAs
// (leave a review, owner dashboard link if they're the owner).
//
// The owner dashboard lives at /groups/:slug/dashboard — accessed via a
// "Manage this listing" pill on this page when the caller is the claim
// holder. Public visitors never see it.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';
import { PostCard } from '../components/PostCard';
import type { Post, Me } from '../types/api';

interface GroupData {
    id:           string;
    slug:         string;
    name:         string;
    type:         string;
    description:  string | null;
    lat:          number | null;
    lon:          number | null;
    address:      string | null;
    phone:        string | null;
    email:        string | null;
    website:      string | null;
    category:     string | null;
    hours:        Record<string, string> | null;
    claim_status: 'unclaimed' | 'pending' | 'claimed' | 'disputed';
    created_at:   string;
    claimed_by_user_id?: string | null;
}

interface Review {
    id: string;
    author_name: string;
    author_avatar?: string;
    rating: number;
    body?: string;
    created_at: string;
}

interface ReviewSummary {
    average: number;
    count:   number;
    my_rating?: number;
    distribution?: Record<string, number>;
}

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
const DAY_LABEL: Record<typeof DAYS[number], string> = {
    mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

export function GroupProfile() {
    const { slug } = useParams<{ slug: string }>();
    const api = useApi();
    const { authed, login } = useAuth();
    const [group,   setGroup]   = useState<GroupData | null>(null);
    const [me,      setMe]      = useState<Me | null>(null);
    const [reviews, setReviews] = useState<Review[]>([]);
    const [summary, setSummary] = useState<ReviewSummary | null>(null);
    const [posts,   setPosts]   = useState<Post[]>([]);
    const [err,     setErr]     = useState<string | null>(null);

    const load = useCallback(async () => {
        if (!slug) return;
        setErr(null);
        // Group + review summary + posts always go through the public
        // endpoints — works for anon AND authed identically. Reviews list
        // uses the authed variant when logged in (returns `my_rating` +
        // the user's own review at the top) and the public variant otherwise.
        try {
            const [g, s, p] = await Promise.all([
                api.get<GroupData>(`/api/public/groups/${encodeURIComponent(slug)}`),
                api.get<ReviewSummary>(`/api/public/groups/${encodeURIComponent(slug)}/review-summary`),
                api.get<{ posts: Post[] }>(`/api/public/groups/${encodeURIComponent(slug)}/posts`).catch(() => ({ posts: [] })),
            ]);
            setGroup(g);
            setSummary(s);
            setPosts(p.posts ?? []);

            const listURL = authed
                ? `/api/groups/${encodeURIComponent(slug)}/reviews`
                : `/api/public/groups/${encodeURIComponent(slug)}/reviews`;
            const r = await api.get<{ reviews: Review[] }>(listURL);
            setReviews(r.reviews ?? []);

            if (authed) {
                try { setMe(await api.get<Me>('/api/me')); } catch { /* fine */ }
            }
        } catch (e) {
            if (e instanceof ApiError && e.status === 404) setErr('not-found');
            else setErr((e as Error).message);
        }
    }, [api, slug, authed]);

    useEffect(() => { load(); }, [load]);

    if (err === 'not-found') return (
        <main className="paper-wrap" style={{ padding: '60px 24px', textAlign: 'center' }}>
            <h1 style={{ fontFamily: 'Newsreader, serif' }}>Not found</h1>
            <p>This listing doesn't exist or isn't public.</p>
            <p><Link to="/">← Back to the paper</Link></p>
        </main>
    );
    if (err) return (
        <main className="paper-wrap" style={{ padding: 40 }}>
            <p style={{ color: '#8b1919' }}>Couldn't load: {err}</p>
        </main>
    );
    if (!group) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    const isOwner       = !!(authed && me && group.claimed_by_user_id && me.id === group.claimed_by_user_id);
    const isUnclaimed   = group.claim_status === 'unclaimed';
    const navHref       = (group.lat != null && group.lon != null)
        ? `https://www.google.com/maps/dir/?api=1&destination=${group.lat},${group.lon}`
        : null;

    return (
        <>
            {!authed && (
                <div className="anon-lure">
                    You're reading as a guest.{' '}
                    <button type="button" onClick={login}>Sign in</button>{' '}
                    to react, comment, and review.
                </div>
            )}

            <main className="article-view">
                <div className="article-view-wrap">
                    <Link to="/" className="back-btn">← Back to the paper</Link>

                    {group.category && <div className="av-kicker">{group.category}</div>}
                    <h1 className="av-headline">{group.name}</h1>
                    {group.description && <p className="av-subhead">{group.description}</p>}

                    <div className="av-byline" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                        {summary && summary.count > 0 ? (
                            <span>
                                <strong style={{ color: 'var(--ink)' }}>★ {summary.average.toFixed(1)}</strong>{' '}
                                · {summary.count} review{summary.count === 1 ? '' : 's'}
                            </span>
                        ) : (
                            <span style={{ fontStyle: 'italic', color: 'var(--muted)' }}>No reviews yet</span>
                        )}
                        {isUnclaimed
                            ? <span style={{ padding: '1px 8px', background: '#faf8f2', border: '1px solid #999', color: '#666', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: '-apple-system, sans-serif' }}>Unclaimed</span>
                            : <span style={{ padding: '1px 8px', background: '#e7f2ec', border: '1px solid #2b5e3f', color: '#2b5e3f', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: '-apple-system, sans-serif' }}>Verified</span>
                        }
                        {isOwner && (
                            <Link to={`/groups/${group.slug}/dashboard`} className="reply-btn"
                                  style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--accent)', textDecoration: 'underline' }}>
                                Manage this listing →
                            </Link>
                        )}
                    </div>

                    {isUnclaimed && (
                        <ClaimBanner slug={group.slug} authed={authed} onSignIn={login} />
                    )}

                    <ContactBlock g={group} navHref={navHref} />

                    {group.hours && Object.keys(group.hours).length > 0 && (
                        <HoursBlock hours={group.hours} />
                    )}

                    {/* Reviews summary + list. "Leave a review" button gates on authed. */}
                    <section style={{ marginTop: 40 }}>
                        <h2 style={{ fontFamily: '-apple-system,sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2.5, borderBottom: '1px solid var(--ink)', paddingBottom: 4 }}>
                            Reviews ({summary?.count ?? 0})
                        </h2>
                        {reviews.length === 0 && (
                            <p style={{ color: '#666', fontStyle: 'italic', marginTop: 12 }}>Be the first to leave a review.</p>
                        )}
                        {reviews.map(r => (
                            <div key={r.id} style={{ padding: '10px 0', borderBottom: '1px dotted var(--line)' }}>
                                <div style={{ fontFamily: 'Newsreader, serif', fontStyle: 'italic', fontSize: 15, lineHeight: 1.5 }}>
                                    {'★'.repeat(r.rating)}{'☆'.repeat(5 - r.rating)}
                                    {r.body && <span> — "{r.body}"</span>}
                                </div>
                                <div style={{ fontFamily: '-apple-system, sans-serif', fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                                    — {r.author_name}, {timeAgo(r.created_at)}
                                </div>
                            </div>
                        ))}
                        <div style={{ marginTop: 12 }}>
                            {authed ? (
                                // Simple prompt-based review composer — good enough for POC.
                                // A proper 5-star selector lands with the follow-up dashboard
                                // work (task #45's owner-side hours editor slice).
                                <button type="button" className="plain" onClick={() => leaveReview(api, group.slug, load)}
                                        style={{ padding: '6px 14px', border: '1px solid var(--ink)', background: 'var(--ink)', color: '#fff', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer' }}>
                                    {summary?.my_rating ? 'Update my review' : 'Leave a review'}
                                </button>
                            ) : (
                                <button type="button" onClick={login} className="reply-btn"
                                        style={{ padding: '6px 14px', border: '1px dashed var(--accent)', background: 'transparent', color: 'var(--accent)', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, cursor: 'pointer' }}>
                                    Sign in to leave a review
                                </button>
                            )}
                        </div>
                    </section>

                    {posts.length > 0 && (
                        <section style={{ marginTop: 40 }}>
                            <h2 style={{ fontFamily: '-apple-system,sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2.5, borderBottom: '1px solid var(--ink)', paddingBottom: 4 }}>
                                Recent posts from {group.name}
                            </h2>
                            {posts.map(p => <PostCard key={p.id} post={p} />)}
                        </section>
                    )}
                </div>
            </main>
        </>
    );
}

async function leaveReview(api: ReturnType<typeof useApi>, slug: string, reload: () => Promise<void>) {
    const raw = window.prompt('Rating (1-5) + optional comment.\n\nExamples:\n  5\n  4 great coffee\n  3 rude staff', '5');
    if (!raw) return;
    const trimmed = raw.trim();
    const m = trimmed.match(/^([1-5])\s*(.*)$/s);
    if (!m) { alert('Rating must start with 1–5, e.g. "5 lovely place"'); return; }
    const rating = parseInt(m[1], 10);
    const body   = m[2].trim();
    try {
        await api.post(`/api/groups/${encodeURIComponent(slug)}/reviews`, { rating, body });
        await reload();
    } catch (e) {
        alert('Review failed: ' + (e as Error).message);
    }
}

function ContactBlock({ g, navHref }: { g: GroupData; navHref: string | null }) {
    const rows: Array<[string, React.ReactNode]> = [];
    if (g.address) rows.push(['Address', navHref
        ? <a href={navHref} target="_blank" rel="noopener noreferrer">{g.address}</a>
        : <span>{g.address}</span>,
    ]);
    if (g.phone)   rows.push(['Phone',   <a href={'tel:' + g.phone}>{g.phone}</a>]);
    if (g.email)   rows.push(['Email',   <a href={'mailto:' + g.email}>{g.email}</a>]);
    if (g.website) rows.push(['Website', <a href={g.website} target="_blank" rel="noopener noreferrer">{g.website}</a>]);
    if (navHref)   rows.push(['Get directions', <a href={navHref} target="_blank" rel="noopener noreferrer">Google Maps →</a>]);
    if (rows.length === 0) return null;
    return (
        <section style={{ marginTop: 24, padding: '16px 0', borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: 14, fontFamily: 'Newsreader, serif' }}>
                <tbody>
                    {rows.map(([label, val]) => (
                        <tr key={label}>
                            <td style={{ paddingRight: 16, color: 'var(--muted)', fontFamily: '-apple-system, sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5, verticalAlign: 'top', paddingTop: 4 }}>{label}</td>
                            <td style={{ padding: '4px 0' }}>{val}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}

function HoursBlock({ hours }: { hours: Record<string, string> }) {
    return (
        <section style={{ marginTop: 24 }}>
            <h2 style={{ fontFamily: '-apple-system,sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 2.5, borderBottom: '1px solid var(--ink)', paddingBottom: 4 }}>
                Hours
            </h2>
            <table style={{ borderCollapse: 'collapse', fontSize: 14, fontFamily: 'Newsreader, serif', marginTop: 8 }}>
                <tbody>
                    {DAYS.map(d => (
                        <tr key={d}>
                            <td style={{ paddingRight: 24, color: 'var(--muted)', fontFamily: '-apple-system, sans-serif', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1.5 }}>{DAY_LABEL[d]}</td>
                            <td>{hours[d] || <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>Closed</span>}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </section>
    );
}

function ClaimBanner({ slug, authed, onSignIn }: { slug: string; authed: boolean; onSignIn: () => void }) {
    return (
        <div style={{
            marginTop: 20, padding: 16,
            background: '#faf8f2', border: '1px dashed #999',
            fontFamily: 'Newsreader, serif',
        }}>
            <div style={{ fontStyle: 'italic', marginBottom: 8 }}>
                <strong>This listing isn't claimed yet.</strong> Whoever owns this business
                can take control of the page — edit the description, add hours, respond to
                reviews.
            </div>
            {authed ? (
                <Link to={`/groups/${slug}/dashboard`} className="reply-btn" style={{ fontFamily: '-apple-system, sans-serif', fontSize: 12, color: 'var(--accent)', fontWeight: 700, textDecoration: 'underline' }}>
                    Claim this listing →
                </Link>
            ) : (
                <button type="button" onClick={onSignIn} className="reply-btn" style={{ fontFamily: '-apple-system, sans-serif', fontSize: 12, color: 'var(--accent)', fontWeight: 700, textDecoration: 'underline', background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>
                    Sign in to claim this listing →
                </button>
            )}
        </div>
    );
}
